/**
 * Fabric Purchasing - Complete solution for fabric products
 * 
 * Features:
 * 1. Quantity observer: Detects changes and updates yardage display
 * 2. Minimum enforcement: Ensures minimum of 1 yard
 * 3. Cart display: Shows yardage instead of unit counts
 * 4. Price display: Shows per-yard price with /yd suffix
 */
(function() {
  'use strict';
  
  var DEFAULT_MIN_QTY = 1;
  
  window.FabricBasePrices = window.FabricBasePrices || {};
  window.FabricProducts = window.FabricProducts || [];
  
  // ============================================
  // YARDAGE HELPERS
  // ============================================
  
  function getYardageText(qty) {
    if (qty === 1) return '1 yd';
    return qty + ' yd';
  }

  function getQuarterYardText(qty) {
    if (!qty || qty <= 0) return '0 yd';
    var wholeYards = Math.floor(qty / 4);
    var remainder = qty % 4;
    var fractions = ['', '1/4', '1/2', '3/4'];
    if (wholeYards === 0) {
      return fractions[remainder] + ' yd';
    }
    if (remainder === 0) {
      return wholeYards + ' yd';
    }
    return wholeYards + ' ' + fractions[remainder] + ' yd';
  }
  
  // Format price in cents to currency string
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // Fallback formatting
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }
  
  // Calculate dynamic price based on quantity
  function calculateDynamicPrice(basePrice, qty) {
    return basePrice * qty;
  }
  
  // ============================================
  // QUANTITY OBSERVER - No property descriptor override
  // ============================================
  
  function setupQuantityObserver(selector) {
    // Skip if already initialized
    if (selector.dataset.fabricObserverInit === 'true') return;
    selector.dataset.fabricObserverInit = 'true';
    
    var input = selector.querySelector('[data-quantity-input]');
    var minusBtn = selector.querySelector('[data-quantity-minus]');
    var plusBtn = selector.querySelector('[data-quantity-plus]');
    var minusWrapper = selector.querySelector('[data-button-wrapper-minus]');
    var wrapper = selector.querySelector('.quantity-selector__wrapper');
    
    if (!input) return;
    
    var isQuarterYard = selector.dataset.quarterYard === 'true';
    
    // Detect cart context - cart inputs have name="updates[]"
    var isCartContext = input.name === 'updates[]' || input.id.indexOf('updates_') === 0;
    
    // Get the dynamic minimum from data attribute (for low inventory situations)
    var minQty = parseInt(selector.dataset.fabricMinQty) || DEFAULT_MIN_QTY;
    var maxQty = parseInt(selector.dataset.fabricMaxQty) || 999;
    var isLowInventory = selector.dataset.fabricLowInventory === 'true';
    
    // Get base price from data attribute (set by Liquid)
    var basePrice = parseInt(selector.dataset.fabricBasePrice) || 0;
    
    // Find dynamic price display element - scope to nearest product form or price container
    var productForm = selector.closest('form[data-product-form]') || selector.closest('.product-form') || selector.closest('.product');
    var priceContainer = productForm ? productForm.querySelector('.price--fabric') : null;
    if (!priceContainer) {
      // Fallback: look for price container as sibling or nearby element
      priceContainer = document.querySelector('.price--fabric');
    }
    var dynamicPriceEl = priceContainer ? priceContainer.querySelector('[data-fabric-dynamic-price]') : document.querySelector('[data-fabric-dynamic-price]');
    var dynamicPriceAmount = dynamicPriceEl ? dynamicPriceEl.querySelector('[data-dynamic-price-amount]') : null;
    var dynamicPriceYardage = dynamicPriceEl ? dynamicPriceEl.querySelector('[data-dynamic-price-yardage]') : null;
    
    // Create yardage overlay if it doesn't exist
    var display = selector.querySelector('.fabric-yardage-overlay');
    if (!display && wrapper) {
      display = document.createElement('span');
      display.className = 'fabric-yardage-overlay';
      display.setAttribute('data-yardage-display', 'true');
      // Insert into wrapper
      wrapper.appendChild(display);
      // Make wrapper position relative for absolute overlay
      wrapper.style.position = 'relative';
    }
    
    // Make input text transparent
    input.style.color = 'transparent';
    input.style.caretColor = 'transparent';
    
    // Position overlay to match input exactly
    function positionOverlay() {
      if (display && input) {
        display.style.position = 'absolute';
        display.style.left = input.offsetLeft + 'px';
        display.style.top = input.offsetTop + 'px';
        display.style.width = input.offsetWidth + 'px';
        display.style.height = input.offsetHeight + 'px';
        display.style.display = 'flex';
        display.style.alignItems = 'center';
        display.style.justifyContent = 'center';
        display.style.pointerEvents = 'none';
        display.style.zIndex = '1';
      }
    }
    positionOverlay();
    // Reposition on window resize
    window.addEventListener('resize', positionOverlay);
    
    var lastValue = input.value;
    
    function updateDisplay() {
      var qty = parseInt(input.value) || minQty;
      
      // In cart context, don't enforce min/max by modifying input value
      // Let Empire's cart handlers manage the actual value
      // Only enforce on product page
      if (!isCartContext) {
        // Enforce minimum (dynamic based on inventory)
        if (qty < minQty) {
          input.value = minQty;
          qty = minQty;
        }
        
        // Enforce maximum inventory limit
        if (qty > maxQty) {
          input.value = maxQty;
          qty = maxQty;
        }
      }
      
      var yardText = isQuarterYard ? getQuarterYardText(qty) : getYardageText(qty);
      
      // Update yardage display
      if (display) {
        display.textContent = yardText;
      }
      
      // Update dynamic price display
      // If base price not set in data attribute, try to get from dynamic price element or FabricBasePrices
      var effectiveBasePrice = basePrice;
      if (!effectiveBasePrice && dynamicPriceEl) {
        // First try: get base price from dynamic price element's data attribute
        var dynamicElBasePrice = parseInt(dynamicPriceEl.dataset.fabricBasePrice);
        if (dynamicElBasePrice) {
          effectiveBasePrice = dynamicElBasePrice;
        } else {
          // Fallback: get from global FabricBasePrices using variant ID from the form
          var variantInput = productForm ? productForm.querySelector('input[name="id"]') : null;
          var variantId = variantInput ? variantInput.value : null;
          if (variantId && window.FabricBasePrices && window.FabricBasePrices[variantId]) {
            effectiveBasePrice = window.FabricBasePrices[variantId];
          }
        }
      }
      
      if (effectiveBasePrice > 0 && dynamicPriceAmount) {
        var dynamicPrice = calculateDynamicPrice(effectiveBasePrice, qty);
        dynamicPriceAmount.innerHTML = formatMoney(dynamicPrice);
      }
      if (dynamicPriceYardage) {
        dynamicPriceYardage.textContent = yardText;
      }
      
      // Update minus button state based on fabric minimum
      if (qty <= minQty) {
        if (minusBtn) minusBtn.disabled = true;
        if (minusWrapper) minusWrapper.classList.add('quantity-selector__button-wrapper--disabled');
      } else {
        if (minusBtn) minusBtn.disabled = false;
        if (minusWrapper) minusWrapper.classList.remove('quantity-selector__button-wrapper--disabled');
      }
      
      // Update plus button state based on max inventory
      var plusWrapper = selector.querySelector('[data-button-wrapper-plus]');
      if (qty >= maxQty) {
        if (plusBtn) plusBtn.disabled = true;
        if (plusWrapper) plusWrapper.classList.add('quantity-selector__button-wrapper--disabled');
      } else {
        if (plusBtn) plusBtn.disabled = false;
        if (plusWrapper) plusWrapper.classList.remove('quantity-selector__button-wrapper--disabled');
      }
      
      lastValue = input.value;
    }
    
    // Intercept minus button clicks to enforce minimum (only on product page)
    // In cart context, let Empire handle button clicks for cart updates
    if (minusBtn && !isCartContext) {
      minusBtn.addEventListener('click', function(e) {
        var currentQty = parseInt(input.value) || minQty;
        if (currentQty <= minQty) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      }, true); // Use capture phase to run before Empire's handler
    }
    
    // Intercept plus button clicks to enforce maximum (only on product page)
    // In cart context, let Empire handle button clicks for cart updates
    if (plusBtn && !isCartContext) {
      plusBtn.addEventListener('click', function(e) {
        var currentQty = parseInt(input.value) || minQty;
        if (currentQty >= maxQty) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      }, true); // Use capture phase to run before Empire's handler
    }
    
    // Initial update
    updateDisplay();
    
    // Listen for input events
    input.addEventListener('input', updateDisplay);
    input.addEventListener('change', updateDisplay);
    
    // Poll for programmatic changes (Empire updates value directly)
    setInterval(function() {
      if (input.value !== lastValue) {
        updateDisplay();
      }
    }, 100);
  }
  
  // ============================================
  // PRICE SUFFIX MONITOR - Ensures /yd is always displayed
  // ============================================
  
  function ensurePriceSuffix() {
    var priceContainers = document.querySelectorAll('.price--fabric .price__current');
    
    priceContainers.forEach(function(container) {
      var priceWrapper = container.closest('.price--fabric');
      var isQuarterYard = priceWrapper && priceWrapper.getAttribute('data-quarter-yard') === 'true';
      var existingSuffix = container.querySelector('.price__unit');
      var correctSuffix = isQuarterYard ? '/1/4 yd' : '/yd';
      if (existingSuffix) {
        if (existingSuffix.textContent !== correctSuffix) {
          existingSuffix.textContent = correctSuffix;
        }
      } else {
        var suffix = document.createElement('span');
        suffix.className = 'price__unit';
        suffix.textContent = correctSuffix;
        container.appendChild(suffix);
      }

      var existingPerYard = container.querySelector('.price__per-yard');
      if (existingPerYard) {
        existingPerYard.remove();
      }
    });
  }
  
  // Watch for price changes and re-add suffix
  function setupPriceObserver() {
    var priceContainers = document.querySelectorAll('.price--fabric [data-price-container]');
    
    priceContainers.forEach(function(container) {
      if (container.dataset.priceObserverInit === 'true') return;
      container.dataset.priceObserverInit = 'true';
      
      var observer = new MutationObserver(function() {
        // Delay slightly to let theme finish updating
        setTimeout(ensurePriceSuffix, 10);
      });
      
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true
      });
    });
  }
  
  // ============================================
  // INITIALIZATION
  // ============================================
  
  function initAll() {
    // JavaScript handles: /yd suffix display + quantity observers for yardage display
    
    // Ensure /yd suffix is present
    ensurePriceSuffix();
    setupPriceObserver();
    
    // Set up quantity observers
    var fabricSelectors = document.querySelectorAll('quantity-selector[data-is-fabric="true"]');
    fabricSelectors.forEach(setupQuantityObserver);
  }
  
  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(initAll, 50);
    });
  } else {
    setTimeout(initAll, 50);
  }
  
  // Re-run on dynamic content
  document.addEventListener('shopify:section:load', function() {
    setTimeout(initAll, 100);
  });
  
  document.addEventListener('theme:cart:change', function() {
    setTimeout(initAll, 100);
  });
  
  // MutationObserver for dynamically added fabric selectors
  var bodyObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType === 1) {
          var selectors = node.querySelectorAll ? 
            node.querySelectorAll('quantity-selector[data-is-fabric="true"]') : [];
          selectors.forEach(setupQuantityObserver);
          
          if (node.matches && node.matches('quantity-selector[data-is-fabric="true"]')) {
            setupQuantityObserver(node);
          }
        }
      });
    });
  });
  
  bodyObserver.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true
  });
  
})();
